import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useRef } from 'react'
import { useRadioGroup, useRadio } from '@react-aria/radio'
import { useRadioGroupState } from '@react-stately/radio'
import { useHover } from '@react-aria/interactions'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { createContext, useContext } from 'react'
import type { RadioGroupState } from '@react-stately/radio'
import Styles from './BrandKitColorSelector.module.scss'
import cx from 'classnames'
import { Edit1 } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

const RadioContext = createContext<RadioGroupState | null>(null)

interface ColorPaletteProps {
  colors: string[]
  isFromWebsite?: boolean
  value: string
  allowEdit?: boolean
  onEdit?: () => void
}

export const ColorPalette = (props: ColorPaletteProps) => {
  const { colors, isFromWebsite = false, value, allowEdit, onEdit } = props
  const state = useContext(RadioContext)
  const ref = useRef(null)

  if (!state) {
    throw new Error('RadioContext not provided')
  }

  let { hoverProps, isHovered } = useHover({})
  const { inputProps } = useRadio(
    {
      ...props,
      'aria-label': `Color palette ${value.replace('palette-', '')}${isFromWebsite ? ' from your website' : ''}`,
    },
    state,
    ref,
  )
  const isSelected = state.selectedValue === value
  return (
    <label className={cx(Styles.colorPalette, { [Styles.selected]: isSelected })}>
      <VisuallyHidden>
        <input {...inputProps} />
      </VisuallyHidden>
      <div ref={ref} className={Styles.paletteContainer} {...hoverProps} data-testid={colors.join(':')}>
        {colors.map((color, index) => (
          <div key={`${color}-${index}`} className={Styles.colorSwatch} style={{ backgroundColor: color }} />
        ))}
      </div>
      {allowEdit && onEdit && isHovered && (
        <div className={Styles.editButton} {...hoverProps}>
          <IconButton variant="secondary" onPress={onEdit} icon={Edit1} size="xs" aria-label="Edit color palette" />
        </div>
      )}
    </label>
  )
}

interface BrandKitColorSelectorProps {
  palettes: string[][]
  selectedPaletteIndex: number
  websitePaletteIndex?: number
  onPaletteSelect: (palette: string[], index: number) => void
  showTitle?: boolean
  allowEdit?: boolean
  onEdit?: (index: number) => void
}

export const BrandKitColorSelector = ({
  palettes,
  selectedPaletteIndex,
  websitePaletteIndex = 0,
  onPaletteSelect,
  showTitle = true,
  allowEdit = false,
  onEdit,
}: BrandKitColorSelectorProps) => {
  const state = useRadioGroupState({
    defaultValue: `palette-${selectedPaletteIndex}`,
    onChange: (selected) => {
      const index = parseInt(selected.replace('palette-', ''))
      onPaletteSelect(palettes[index], index)
    },
  })

  const { radioGroupProps, labelProps } = useRadioGroup(
    {
      'aria-label': 'Color palettes',
    },
    state,
  )

  return (
    <div className={Styles.brandKitColorSelector}>
      <div className={Styles.heading}>
        {showTitle && (
          <Heading level={4} {...labelProps}>
            Pick your Colors
          </Heading>
        )}
        <Text variant="metadata" color="var(--dark-60)">
          We will use them to customize your designs
        </Text>
      </div>

      <div {...radioGroupProps} className={Styles.paletteGrid}>
        <RadioContext.Provider value={state}>
          {palettes.map((palette, index) => (
            <ColorPalette
              key={`palette-${index}`}
              value={`palette-${index}`}
              colors={palette}
              isFromWebsite={index === websitePaletteIndex}
              allowEdit={allowEdit}
              onEdit={() => onEdit?.(index)}
            />
          ))}
        </RadioContext.Provider>
      </div>
    </div>
  )
}
